import React, { Component, PropTypes } from 'react';
import { connect } from 'react-redux';
import * as d3 from 'd3';
import touristsData from '../../data/open_data_3_beta.csv';
import { selectMonth, selectSimilarExRateMonths } from '../actions/index';

import DateSelector from './date_selector';


const chartMargin = { top: 40, right: 40, bottom: 40, left: 70 };
const chartTotalWidth = 640;
const chartTotalHeight = 480 * 0.7;
const mainChartWidth = chartTotalWidth - chartMargin.right - chartMargin.left;
const mainChartHeight = chartTotalHeight - chartMargin.top - chartMargin.bottom;

class LineChart extends Component {
  constructor(props) {
    super(props);
    this.isSelected = this.isSelected.bind(this);
    this.renderSelectedMonthInfo = this.renderSelectedMonthInfo.bind(this);
    this.selectSimilarExRateMonths = this.selectSimilarExRateMonths.bind(this);
  }

  componentDidMount() {
    const scaleX = d3.scaleLinear()
                     .range([0, mainChartWidth])
                     .domain([1, 12]);

    const scaleY = d3.scaleLinear()
                     .range([0, mainChartHeight])
                     .domain([1050000, 650000]);

    const axisX = d3.axisTop(scaleX)
                    .tickFormat(d => `${d}月`)
                    .tickSize(0, 0);

    const axisY = d3.axisLeft(scaleY)
                    .ticks(5)
                    .tickSizeInner(-mainChartWidth, 0)
                    .tickValues([650000, 750000, 850000, 950000, 1050000]);

    const drawLine = d3.line()
      .x(datum => scaleX(datum.month))
      .y(datum => scaleY(datum.total));

    const svg = d3.select('#line-chart')
      .append('div')
      .attr('class', 'svg-container')
      .append('svg')
      .attr('preserveAspectRatio', 'xMinYMin meet')
      .attr('viewBox', `0 0 ${chartTotalWidth} ${chartTotalHeight}`)
    .append('g')
      .attr('transform', `translate(${chartMargin.left},${chartMargin.top})`);

    // set y axis
    const yAxis = svg.append('g')
                     .call(axisY)
                     .attr('class', 'axis -y');

    yAxis.selectAll('text')
         .attr('class', 'text')
         .attr('transform', `translate(${-10},0)`);

    yAxis.selectAll('.tick line')
        .attr('class', 'grid-line');


    // set x Axis
    const xAxis = svg.append('g')
                     .call(axisX)
                     .attr('class', 'axis -x');

    xAxis.selectAll('text')
         .attr('class', 'text')
         .attr('transform', `translate(0,${-10})`);


    // add chart: line and dot
    Object.keys(this.props.displayedYear).forEach(year => {
      const data = touristsData
          .filter(d => d.year === year);

      let delta = 0;
      switch (year) {
        case '103':
          delta = 40;
          break;
        default:
          break;
      }

      svg.append('path')
        .attr('id', `line-y${year}`)
        .attr('class', 'year-line')
        .attr('d', drawLine(data))
        .attr('y', 0)
        .attr('transform', `translate(0, ${delta})`);

      svg.selectAll('dot')
        .data(data)
      .enter().append('circle')
        .attr('r', 5)
        .attr('cx', datum => scaleX(datum.month))
        .attr('cy', datum => scaleY(datum.total))
        .attr('id', datum => `dot-${year}-${datum.month}`)
        .attr('class', datum => `line-dot dot-y${year} dot-m${datum.month}`)
        .attr('transform', `translate(0, ${delta})`);
    });

    // querySelectorAll return nodelist, not array
    [].forEach.call(document.querySelectorAll('.line-dot'), dot => {
      dot.addEventListener('click', (event) => {
        event.stopPropagation();
        const [no, y, m] = dot.getAttribute('id').split('-');
        this.props.selectMonth({ year: y, month: m });
      });
    });

    this.props.selectMonth({ year: '105', month: '8' });
  }

  componentDidUpdate() {
    Object.keys(this.props.displayedYear).forEach(year => {
      if (this.props.displayedYear[year]) {
        d3.selectAll(`#line-y${year},.dot-y${year}`)
          .style('display', 'block');
      } else {
        d3.selectAll(`#line-y${year},.dot-y${year}`)
          .style('display', 'none');
      }
    });

    [].forEach.call(document.querySelectorAll('.line-dot'), dot => {
      const [no, y, m] = dot.getAttribute('id').split('-');
      const dotClass = dot.getAttribute('class').split(' ');
      const dotIsSelected = this.isSelected(y, m);
      const selectedIndex = dotClass.findIndex(n => n === '-selected');
      const haveSelectedClass = selectedIndex !== -1;
      if (dotIsSelected && !haveSelectedClass) {
        dotClass.push('-selected');
      } else if (!dotIsSelected && haveSelectedClass) {
        dotClass.splice(selectedIndex, 1);
      }
      dot.setAttribute('class', dotClass.join(' '));
    });
  }

  isSelected(y, m) {
    return this.props.comparedMonths.find(d =>
      d.year === y && d.month === m);
  }

  selectSimilarExRateMonths() {
    this.props.selectSimilarExRateMonths(this.props.selectedMonth.avgExchangeRate);
  }

  renderSelectedMonthInfo() {
    const monthInfo = this.props.selectedMonth;
    return monthInfo ?
      <div className="selected-month">
        <span className="info">
          {`${monthInfo.year}年 ${monthInfo.month}月`}
        </span>
        <span className="info">
          <span className="info">
            {`來臺人數: ${monthInfo.total}`}
          </span>
          <span className="info">
            {`美金匯率: ${parseFloat(monthInfo.avgExchangeRate).toFixed(2)}`}
          </span>
        </span>
        <span className="info -tip">
          欲查詢相似匯率(+-0.2)，請先點選圖內代表各月份支援圈，再點擊右側按鈕
        </span>
        <button
          type="button"
          onClick={this.selectSimilarExRateMonths}
          className="btn -relatedmonth"
        >
          看相似匯率
        </button>
      </div> : null;
  }

  render() {
    return (
      <div className="chart-block">
        <div className="container">
          <span className="desktop-only -inline-block">
            <DateSelector />
          </span>
          {this.renderSelectedMonthInfo()}
          <div id="line-chart"></div>
          <span className="mobile-only -inline-block">
            <DateSelector />
          </span>
        </div>
      </div>
    );
  }
}

LineChart.propTypes = {
  selectMonth: PropTypes.func,
  displayedYear: PropTypes.object,
  selectedMonth: PropTypes.object,
  comparedMonths: PropTypes.array,
  selectSimilarExRateMonths: PropTypes.func,
};

const mapStateToProps = (state) => ({
  displayedYear: state.date.displayedYear,
  selectedMonth: state.date.selectedMonth,
  comparedMonths: state.date.comparedMonths,
});

export default connect(mapStateToProps, { selectMonth, selectSimilarExRateMonths })(LineChart);



/** WEBPACK FOOTER **
 ** ./src/components/line_chart.js
 **/